/**
 * Pure model of the teacher roster (`TeachersPage`, tasks T.F6 + T.R7) — framework-free
 * on purpose, the same rule `quotas-model.ts` and `combobox-model.ts` state: the `unit`
 * Playwright project runs in plain Node, where a component file would drag React along.
 *
 * Everything here is the URL/state arithmetic and the form validation of ONE screen.
 * Nothing knows about `fetch` or `@tanstack/react-query`; the api half of the screen
 * lives in `../../api/teachers`.
 */

import type { Role } from '../../auth/roles';
import {
  CURATOR_HIDDEN_ATTRIBUTE_KEYS,
  type TeacherAttributes,
  type TeacherCreatedVia,
  type TeacherDto,
  type TeacherStatus,
} from '../../api/teachers';

// ---------------------------------------------------------------------------
// URL params
// ---------------------------------------------------------------------------

/** Every filter the roster keeps in the query string, so a link round-trips. */
export const PARAM = {
  VENUE_ID: 'venueId',
  Q: 'q',
  /**
   * REMOVED (v1.9.11-adjacent, T.Z72 — owner): «Отдел» has left this page (it was
   * never the question a teacher roster answers — the venues page keeps it). The api
   * key `?department=` on #7.5 stands (the frozen contract keeps the key), the page
   * just stopped sending it; a hand-written `?department=…` URL degrades to "absent"
   * like any unknown parameter, never to a 400.
   */
  /** ADDITIVE (v1.9.17, T.Z90) — the active funnel chip (`?chip=confirmed` …). */
  CHIP: 'chip',
  PAGE: 'page',
  /** T.R7 — the row drawer: `/teachers?teacherId=N`, the applications `?pguId=` pattern. */
  TEACHER_ID: 'teacherId',
} as const;

/** The `<form>` drafts, as the user typed them, before «Применить». */
export interface TeacherListParams {
  venueId?: number;
  q?: string;
  /** ADDITIVE (v1.9.17, T.Z90) — the active chip, when one is. */
  chip?: TeacherChip;
  page: number;
  /** The drawer's row, when one is open. */
  teacherId?: number;
}

/**
 * Positive integer or `undefined`. Like `lib/query-params.toInt`, but stricter about
 * what counts as a number: only a pure digit string parses, so `1.5` and `12abc`
 * degrade to "absent" instead of silently becoming `1` and `12` (`parseInt` would).
 * The combobox and the URL are the two inputs, and neither can produce anything else.
 */
export function toPositiveInt(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const text = value.trim();
  if (!/^\d+$/.test(text)) return undefined;
  const parsed = Number.parseInt(text, 10);
  return parsed > 0 ? parsed : undefined;
}

/** URL → params. A junk value degrades to "absent", never to a broken request. */
export function parseTeacherParams(params: Record<string, string>): TeacherListParams {
  const venueId = toPositiveInt(params[PARAM.VENUE_ID]);
  const page = toPositiveInt(params[PARAM.PAGE]) ?? 1;
  const teacherId = toPositiveInt(params[PARAM.TEACHER_ID]);
  const q = params[PARAM.Q]?.trim() || undefined;
  const chip = parseTeacherChip(params[PARAM.CHIP]);
  return {
    venueId,
    page,
    ...(q !== undefined ? { q } : {}),
    ...(chip !== undefined ? { chip } : {}),
    ...(teacherId !== undefined ? { teacherId } : {}),
  };
}

/** `true` when at least one filter narrows the roster — the «Сбросить» affordance. */
export function teacherFiltersOn(params: TeacherListParams): boolean {
  return Boolean(
    params.venueId !== undefined || params.q !== undefined || params.chip !== undefined || params.page > 1,
  );
}

// ---------------------------------------------------------------------------
// Combobox options
// ---------------------------------------------------------------------------

/** The one option shape the venue combobox needs (value = the venue id as text). */
export interface VenueOption {
  value: string;
  label: string;
  /** The normalized address — searchable secondary text, never shown in the input. */
  hint?: string;
}

export interface VenueDirectoryEntryLike {
  id: number;
  name: string;
  address: string | null;
}

/** The dictionary → combobox options, in the order the api answered (name asc). */
export function venueComboboxOptions(entries: readonly VenueDirectoryEntryLike[]): VenueOption[] {
  return entries.map((venue) => ({
    value: String(venue.id),
    label: venue.name,
    ...(venue.address ? { hint: venue.address } : {}),
  }));
}

/**
 * The venue column of the roster. A teacher whose venue id is not in the loaded
 * dictionary (a scoped dictionary fetch, a venue created after it loaded) still shows
 * SOMETHING — the resolved `venueName` the api carries — so the column never reads as
 * "no venue" when the api says otherwise.
 */
export function venueLabelOf(teacher: TeacherDto, options: readonly VenueOption[]): string {
  const fromDictionary = options.find((option) => option.value === String(teacher.venueId))?.label;
  return fromDictionary ?? teacher.venueName;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.11, T.Z69/T.Z71) — the directory read's states and the КУРАТОР scope
// ---------------------------------------------------------------------------

/** The three states the #6 directory read can leave the page's venue pickers in. */
export type VenueDirectoryState = { kind: 'loading' | 'error' | 'ready' };

/**
 * The sentence the page prints when the #6 directory read FAILED. T.Z69's lesson: the
 * api's 403 on the undecorated dictionary route used to fold into
 * `data ?? []` and the combobox said «Ничего не найдено» — a data statement over what
 * was really a refusal. A failure is an error state with «Повторить», never an empty
 * options list; a SUCCESSFUL read that answers zero venues stays `ready`.
 */
export const TEACHER_VENUE_DIRECTORY_ERROR = 'Не удалось загрузить список площадок.';

/** Query flags → state. Mirrors the cabinet's `prefillDictionaryState` (T.Z64). */
export function venueDirectoryState(query: {
  isPending?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
}): VenueDirectoryState {
  if (query.isError === true) return { kind: 'error' };
  if (query.isSuccess === true) return { kind: 'ready' };
  return { kind: 'loading' };
}

/**
 * ADDITIVE (v1.9.11, T.Z71) — who may write here. A МЕНЕДЖЕР/АДМИН as always; ADDITIVE
 * a КУРАТОР-role session (the `VIEWER` enum value) holding at least one cabinet venue —
 * the api admits exactly that shape on #7.5's POST/PATCH and enforces the venue scope
 * itself (a foreign venue is 403), so this is a control-visibility gate, never the
 * boundary. Fail-closed on both ends: an absent role writes nothing, a link-less
 * КУРАТОР (invalid per D21, 403 on the wire) sees the read-only page.
 */
export function canWriteTeachers(role: Role | null, cabinetVenueIds: ReadonlyArray<number>): boolean {
  if (role === 'MANAGER' || role === 'ADMIN') return true;
  return role === 'VIEWER' && cabinetVenueIds.length > 0;
}

/**
 * ADDITIVE (v1.9.11, T.Z71) — the «Площадка» options of the CREATE and EDIT forms,
 * locked to the КУРАТОР's cabinet scope so a submit that cannot succeed never leaves
 * the browser. Staff roles keep the whole dictionary (a manager may place a teacher on
 * any venue, as before). The api's 403 stays the authority — this only hides the
 * options the write would be refused for.
 */
export function venueOptionsInScope(
  options: readonly VenueOption[],
  role: Role | null,
  cabinetVenueIds: ReadonlyArray<number>,
): VenueOption[] {
  if (role !== 'VIEWER') return [...options];
  const scope = new Set(cabinetVenueIds);
  return options.filter((option) => scope.has(Number(option.value)));
}

/**
 * ADDITIVE (T.Z77) — the «Площадка» the CREATE form opens on. When the page's filter
 * holds a venue the form can actually OFFER (an option of the scope-locked list), the
 * form starts there — the owner's «добавляю педагога, площадка уже выбрана». Anything
 * else — no filter, a venue outside a КУРАТОР's scope (a deep link could name one), a
 * dictionary that has not loaded — leaves the field empty: a draft the api would refuse
 * with 403 must not ride in through the prefill, the same rule `venueOptionsInScope`
 * applies to the picker.
 */
export function createVenuePrefill(paramsVenueId: number | undefined, options: readonly VenueOption[]): string {
  if (paramsVenueId === undefined) return '';
  const value = String(paramsVenueId);
  return options.some((option) => option.value === value) ? value : '';
}

/**
 * ADDITIVE (v1.9.12, T.Z80) — the «Удалить» visibility gate. The api opened #7.5's
 * DELETE to the same `'scopedCurator'` shape as the other writes, so the SAME predicate
 * decides what this page shows: a МЕНЕДЖЕР/АДМИН (unscoped, as always), a КУРАТОР with
 * a non-empty cabinet scope. Deliberately an alias, not a second truth — the delete and
 * the edit must appear and disappear together, exactly as the api admits them.
 * ADDITIVE (v1.9.17, T.Z89) — the button's MEANING is per role now: an ADMIN removes,
 * a МЕНЕДЖЕР/КУРАТОР marks «на удаление» (`teacherDeleteCopy` says which).
 */
export const canDeleteTeachers = canWriteTeachers;

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.17, T.Z89) — the status: the wire's raw column, the screen's RU
// wording, and the per-role vocabulary the api enforces anyway.
// ---------------------------------------------------------------------------

/** «подтверждён» / «не подтверждён» / «на удаление» — the owner's own three names. */
export const TEACHER_STATUS_LABELS: Record<TeacherStatus, string> = {
  CONFIRMED: 'подтверждён',
  UNCONFIRMED: 'не подтверждён',
  FOR_DELETION: 'на удаление',
};

export function statusLabel(status: TeacherStatus): string {
  return TEACHER_STATUS_LABELS[status] ?? status;
}

/** The badge's colour hook (the CSS decides what it looks like). */
export type TeacherStatusAccent = 'confirmed' | 'unconfirmed' | 'deletion';

export function statusAccent(status: TeacherStatus): TeacherStatusAccent {
  if (status === 'CONFIRMED') return 'confirmed';
  return status === 'FOR_DELETION' ? 'deletion' : 'unconfirmed';
}

/**
 * The status vocabulary of a session, mirrored from the api's rule: an ADMIN and a
 * МЕНЕДЖЕР may set any of the three; a КУРАТОР may set NONE from a status editor —
 * their «на удаление» goes through the «Удалить» button, which the DELETE endpoint
 * turns into the mark for them. An empty list = no editor on the drawer.
 */
export function statusOptionsFor(role: Role | null): TeacherStatus[] {
  if (role === 'ADMIN' || role === 'MANAGER') return ['CONFIRMED', 'UNCONFIRMED', 'FOR_DELETION'];
  return [];
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.17, T.Z90) — the funnel chips (the «Педагоги» page only, never the
// dashboard). A SINGLE-SELECT row in the cabinet's summary-chips shape: a click
// filters the table, a click on the active chip clears it, «всего» is the unfiltered
// view. The counts come from `meta.counts` — the api's funnel over the same venue /
// venue-set / department / search scope, deliberately ignoring the active chip.
// ---------------------------------------------------------------------------

/**
 * The four chips. The buckets, stated:
 *   * `confirmed` — `status = CONFIRMED` («подтверждено»);
 *   * `awaiting`  — `status IN (UNCONFIRMED, FOR_DELETION)` («ожидают подтверждения»):
 *     BOTH states await a person's decision — a curator's delete request awaits the
 *     ADMIN's ruling exactly as an unconfirmed hand-add awaits a first confirmation —
 *     which makes «подтверждено» + «ожидают» = «всего», a partition;
 *   * `curator`   — `createdVia = CURATOR` («добавлены кураторами»): the PROVENANCE
 *     axis, deliberately overlapping the status ones (a curator's confirmed row is
 *     both), which is why the chips are single-select and not a checkbox set.
 */
export type TeacherChip = 'confirmed' | 'awaiting' | 'curator';

export const TEACHER_CHIPS: ReadonlyArray<{ value: TeacherChip; label: string; title: string }> = [
  { value: 'confirmed', label: 'Подтверждено', title: 'Статус «подтверждён»' },
  { value: 'awaiting', label: 'Ожидают подтверждения', title: '«не подтверждён» + «на удаление»' },
  { value: 'curator', label: 'Добавлены кураторами', title: 'Источник: добавлены куратором' },
];

/** URL token → chip. A token this build does not know degrades to "absent". */
export function parseTeacherChip(raw: string | undefined): TeacherChip | undefined {
  if (raw === undefined) return undefined;
  return TEACHER_CHIPS.some((chip) => chip.value === raw) ? (raw as TeacherChip) : undefined;
}

/** The chip's query half: which of the two api filters it asks for. */
export function chipQueryOf(chip: TeacherChip): { status?: TeacherStatus[]; createdVia?: TeacherCreatedVia } {
  if (chip === 'confirmed') return { status: ['CONFIRMED'] };
  if (chip === 'awaiting') return { status: ['UNCONFIRMED', 'FOR_DELETION'] };
  return { createdVia: 'CURATOR' };
}

/**
 * The chip's number off `meta.counts`. `null` when the api predates v1.9.17 (no
 * counts) — the chip then renders without a number instead of lying with a zero.
 */
export function chipCountOf(
  chip: TeacherChip | undefined,
  counts: { total: number; confirmed: number; unconfirmed: number; forDeletion: number; curatorAdded: number } | undefined,
): number | null {
  if (counts === undefined) return null;
  if (chip === undefined || chip === 'confirmed') return counts.confirmed;
  if (chip === 'awaiting') return counts.unconfirmed + counts.forDeletion;
  return counts.curatorAdded;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.17, T.Z91) — the КУРАТОР projection's web half: the api already
// omits the ten cells, the page stops RENDERING them, and one helper decides by ROLE.
// ---------------------------------------------------------------------------

/** A КУРАТОР session — the VIEWER enum value (the D21 single-role reading). */
export function isCuratorRole(role: Role | null): boolean {
  return role === 'VIEWER';
}

/** The drawer sections the session may see: for a КУРАТОР, «КПК», «Договор» and «Документы и согласия» are gone. */
export function attributeSectionsFor(role: Role | null): ReadonlyArray<TeacherAttributeSection> {
  if (!isCuratorRole(role)) return ATTRIBUTE_SECTIONS;
  return ATTRIBUTE_SECTIONS.filter((section) => section.key !== 'kpk' && section.key !== 'contract' && section.key !== 'documents');
}

/** The attribute fields the session may see — the same cut, flat (the drafts iterate this). */
export function attributeFieldsFor(role: Role | null): ReadonlyArray<TeacherAttributeField> {
  if (!isCuratorRole(role)) return ATTRIBUTE_FIELDS;
  const hidden: ReadonlySet<string> = new Set<string>(CURATOR_HIDDEN_ATTRIBUTE_KEYS);
  return ATTRIBUTE_FIELDS.filter((field) => !hidden.has(field.key));
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.12, T.Z80) — the delete's two layers, the cabinet's #26.4 shape:
// «Удалить» REPLACES the editor drawer with the confirmation drawer (the overlays are
// never nested — `PrefillOnlineBlock` unmounts the form drawer the same way), and the
// network request can only be PRODUCED from the confirmed layer. That last property is
// what `deleteRequestId` exists for: the component calls it inside the confirm button,
// so the unit project can pin the gating without a browser.
// ---------------------------------------------------------------------------

/** The editor layer (the row drawer with its form) and the confirmation over it. */
export type TeacherDeleteLayer = { kind: 'editor' } | { kind: 'confirm'; teacher: TeacherDto };

export const EDITOR_DELETE_LAYER: TeacherDeleteLayer = { kind: 'editor' };

/** The «Удалить» click: swap the editor for the confirmation carrying that row. */
export function toDeleteConfirm(teacher: TeacherDto): TeacherDeleteLayer {
  return { kind: 'confirm', teacher };
}

/**
 * The ONE producer of the delete request: the editor layer answers `null` (nothing
 * leaves the browser), the confirmed layer answers the row's id.
 */
export function deleteRequestId(layer: TeacherDeleteLayer): number | null {
  return layer.kind === 'confirm' ? layer.teacher.id : null;
}

/** The confirmation drawer's copy: the name to confirm against, and the consequences. */
export interface TeacherDeleteCopy {
  title: string;
  description: string;
  /** The row the actor is about to remove, by ФИО and venue. */
  body: string;
  /** What the delete does to the rest of the system — the consequence sentence. */
  hint: string;
}

/**
 * The confirmation wording — per ROLE since v1.9.17 (T.Z89), because the two answers
 * of `DELETE /teachers/:id` are different facts and the actor must be told which one
 * they are asking for:
 *
 *   * an **ADMIN** keeps the hard delete: the ФИО is the identifying fact, the
 *     consequences say the row LEAVES the registry (irreversible), and that the
 *     учебные группы it led stay without a педагог (`ON DELETE SET NULL`, migration
 *     0024) — never that the data is quietly erased;
 *   * a **МЕНЕДЖЕР/КУРАТОР** is MARKING, not deleting: the drawer says the row gets
 *     the status «на удаление» and STAYS until an ADMIN rules — a request, not an
 *     erasure, and exactly the process ГПХ/сетевые договоры demand.
 */
export function teacherDeleteCopy(teacher: Pick<TeacherDto, 'fullName' | 'venueName'>, role: Role | null): TeacherDeleteCopy {
  if (role === 'ADMIN') {
    return {
      title: 'Удалить педагога?',
      description: `Площадка «${teacher.venueName}». Удаление необратимо.`,
      body: `Строка «${teacher.fullName}» будет удалена из реестра педагогов.`,
      hint: 'Учебные группы, где этот педагог был назначен, останутся без педагога. Вернуть строку можно повторным импортом площадки или вводом заново.',
    };
  }
  return {
    title: 'Пометить педагога «на удаление»?',
    description: `Площадка «${teacher.venueName}». Строка останется в реестре.`,
    body: `Строке «${teacher.fullName}» будет установлен статус «на удаление».`,
    hint: 'Строка останется на месте до решения администратора — окончательно удаляет только он. Свяжитесь с администратором, если удалить нужно сейчас.',
  };
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/** The phone is stored verbatim; an absent one prints an em dash, never «null». */
export function phoneLabel(phone: string | null): string {
  return phone ?? '—';
}

export function emailLabel(email: string | null): string {
  return email ?? '—';
}

/** RU plural of «педагог» for the toolbar counter. */
export function teacherCountLabel(total: number): string {
  const mod10 = total % 10;
  const mod100 = total % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'педагог' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'педагога' : 'педагогов';
  return `${total} ${word}`;
}

/** «Страница 1 из 4» arithmetic; `totalPages` is 1 for an empty roster, never 0. */
export function totalPages(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

// ---------------------------------------------------------------------------
// The T.R7 attribute block — how the drawer groups and labels the 19 columns
// ---------------------------------------------------------------------------

export interface TeacherAttributeField {
  key: keyof TeacherAttributes;
  /** The sheet's own wording, trimmed of its parenthetical instructions. */
  label: string;
  /** The sheet writes some of these cells across several lines — those render as a textarea. */
  multiline?: boolean;
}

/**
 * The drawer's sections. The owner grouped them «Занятость / КПК / Договор / Документы»
 * and every one of the 19 columns has to be editable there — «Образование и опыт» is
 * the one extra fold, because nine fields in a single section do not read.
 */
export interface TeacherAttributeSection {
  key: string;
  title: string;
  fields: ReadonlyArray<TeacherAttributeField>;
}

export const ATTRIBUTE_FIELDS: ReadonlyArray<TeacherAttributeField> = [
  { key: 'learningFormat', label: 'Формат обучения' },
  { key: 'employmentStatus', label: 'Статус педагога (ТД, ГПХ, СЗ)' },
  { key: 'engagementBasis', label: 'Основание привлечения' },
  { key: 'mainWorkplace', label: 'Основное место работы, должность' },
  { key: 'education', label: 'Образование (заведение, специальность, год)', multiline: true },
  { key: 'academicDegree', label: 'Ученая степень / звание' },
  { key: 'dpoLast3Years', label: 'ДПО по профилю за последние 3 года', multiline: true },
  { key: 'itExperience', label: 'Опыт в ИТ, ИИ или робототехнике', multiline: true },
  { key: 'dopParticipation', label: 'Участие в реализации ДОП' },
  { key: 'kpkAttended', label: 'КПК' },
  { key: 'kpkDates', label: 'Даты КПК' },
  { key: 'courseLinksSent', label: 'Отправлена ссылка на курсы', multiline: true },
  { key: 'appliedOnSite', label: 'Подал заявку на сайте (Педагог.13 DL 12/09)' },
  { key: 'docsSubmitted', label: 'Сданы документы на КПК' },
  { key: 'contractSigned', label: 'Договор подписан Исполнителем' },
  { key: 'contractDate', label: 'Дата договора' },
  { key: 'esignature', label: 'Электронная подпись (да/нет)' },
  { key: 'pdConsent', label: 'Согласие на обработку персональных данных' },
  { key: 'docsFolderUrl', label: 'Ссылка на папку с документами (Диск)' },
] as const;

export const ATTRIBUTE_SECTIONS: ReadonlyArray<TeacherAttributeSection> = [
  {
    key: 'employment',
    title: 'Занятость',
    fields: ATTRIBUTE_FIELDS.filter((field) =>
      ['learningFormat', 'employmentStatus', 'engagementBasis', 'mainWorkplace'].includes(field.key),
    ),
  },
  {
    key: 'education',
    title: 'Образование и опыт',
    fields: ATTRIBUTE_FIELDS.filter((field) =>
      ['education', 'academicDegree', 'dpoLast3Years', 'itExperience', 'dopParticipation'].includes(field.key),
    ),
  },
  {
    key: 'kpk',
    title: 'КПК',
    fields: ATTRIBUTE_FIELDS.filter((field) =>
      ['kpkAttended', 'kpkDates', 'courseLinksSent', 'appliedOnSite', 'docsSubmitted'].includes(field.key),
    ),
  },
  {
    key: 'contract',
    title: 'Договор',
    fields: ATTRIBUTE_FIELDS.filter((field) => ['contractSigned', 'contractDate'].includes(field.key)),
  },
  {
    key: 'documents',
    title: 'Документы и согласия',
    fields: ATTRIBUTE_FIELDS.filter((field) => ['esignature', 'pdConsent', 'docsFolderUrl'].includes(field.key)),
  },
];

/** «да» / «нет»-style cells read as-is; an absent one is an em dash, never «null». */
export function attributeLabel(value: string | null): string {
  return value === null || value.trim() === '' ? '—' : value;
}

// ---------------------------------------------------------------------------
// The add/edit form
// ---------------------------------------------------------------------------

export interface TeacherDraft {
  venueId: string;
  fullName: string;
  phone: string;
  email: string;
  /** The T.R7 attribute block, as the user typed it (an absent column is `''`). */
  attributes: TeacherAttributeDraft;
}

/** The 19 sheet attributes, as `<input>`/`<textarea>` strings (`null` → `''`). */
export type TeacherAttributeDraft = Record<keyof TeacherAttributes, string>;

export const EMPTY_DRAFT: TeacherDraft = {
  venueId: '',
  fullName: '',
  phone: '',
  email: '',
  attributes: emptyAttributeDraft(),
};

export function emptyAttributeDraft(): TeacherAttributeDraft {
  return Object.fromEntries(ATTRIBUTE_FIELDS.map((field) => [field.key, ''])) as TeacherAttributeDraft;
}

/** A teacher row → the form draft (the contacts as-is; «—» is a display concern). */
export function draftOf(teacher: TeacherDto): TeacherDraft {
  return {
    venueId: String(teacher.venueId),
    fullName: teacher.fullName,
    phone: teacher.phone ?? '',
    email: teacher.email ?? '',
    attributes: draftAttributesOf(teacher),
  };
}

/** The row's attribute block → draft strings (`null` is an empty field, never «null»). */
export function draftAttributesOf(teacher: TeacherDto): TeacherAttributeDraft {
  return Object.fromEntries(
    ATTRIBUTE_FIELDS.map((field) => [field.key, teacher[field.key] ?? '']),
  ) as TeacherAttributeDraft;
}

/** Per-field messages; `undefined` = valid. Only the fields the user can fix. */
export type DraftErrors = Partial<Record<'venueId' | 'fullName' | 'phone' | 'email' | keyof TeacherAttributes, string>>;

/**
 * The client half of the api's validation (`apps/api/src/teachers/dto.ts`). The api
 * stays the authority — this exists so a submit that cannot succeed never leaves the
 * browser, and so the errors render under the field they name.
 */
export function validateDraft(draft: TeacherDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (toPositiveInt(draft.venueId) === undefined) errors.venueId = 'Выберите площадку';
  if (draft.fullName.trim() === '') errors.fullName = 'Укажите ФИО';
  else if (draft.fullName.trim().length > 512) errors.fullName = 'Не длиннее 512 символов';
  if (draft.phone.trim() !== '' && draft.phone.trim().length > 64) errors.phone = 'Не длиннее 64 символов';
  if (draft.email.trim() !== '') {
    if (draft.email.trim().length > 320) errors.email = 'Не длиннее 320 символов';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) errors.email = 'Похоже, это не адрес почты';
  }
  // The api caps every attribute cell at 4000 characters — the sheet cells are prose,
  // and a pasted novel would be refused by the server anyway.
  for (const field of ATTRIBUTE_FIELDS) {
    if (draft.attributes[field.key].length > 4000) errors[field.key] = 'Не длиннее 4000 символов';
  }
  return errors;
}

export const draftHasErrors = (errors: DraftErrors): boolean => Object.keys(errors).length > 0;

/** The draft → the create body (`phone`/`email` become `null` when left empty). */
export function toCreateBody(draft: TeacherDraft): {
  venueId: number;
  fullName: string;
  phone: string | null;
  email: string | null;
} {
  return {
    venueId: Number(draft.venueId),
    fullName: draft.fullName.trim().replace(/\s+/g, ' '),
    phone: draft.phone.trim() === '' ? null : draft.phone.trim(),
    email: draft.email.trim() === '' ? null : draft.email.trim().toLowerCase(),
  };
}

/**
 * The draft → the MINIMAL patch: only the keys that actually differ from the row, so a
 * submit that changes nothing produces the api's documented no-op (200, no audit row)
 * instead of rewriting contacts or attributes the user never touched. An emptied
 * attribute cell clears the column (`null`), the same reading every nullable key has.
 *
 * ADDITIVE (v1.9.17, T.Z91) — `fields` narrows the attribute half to what the session
 * may edit (a КУРАТОР's reduced block). It matters even though the form never renders
 * a hidden field: a hidden cell arrives ABSENT from a curator row, so its draft value
 * is `''` while the row's truth is "not for your role" — comparing it against
 * `undefined` would wrongly emit a `null` clear and the api would refuse it with 403.
 */
export function toPatchBody(
  teacher: TeacherDto,
  draft: TeacherDraft,
  fields: ReadonlyArray<TeacherAttributeField> = ATTRIBUTE_FIELDS,
): Record<string, string | number | null> {
  const patch: Record<string, string | number | null> = {};
  const venueId = Number(draft.venueId);
  if (venueId !== teacher.venueId) patch['venueId'] = venueId;
  const fullName = draft.fullName.trim().replace(/\s+/g, ' ');
  if (fullName !== teacher.fullName) patch['fullName'] = fullName;
  const phone = draft.phone.trim();
  if (phone !== (teacher.phone ?? '')) patch['phone'] = phone === '' ? null : phone;
  const email = draft.email.trim().toLowerCase();
  if (email !== (teacher.email ?? '')) patch['email'] = email === '' ? null : email;
  for (const field of fields) {
    const value = draft.attributes[field.key].trim();
    if (value !== (teacher[field.key] ?? '')) patch[field.key] = value === '' ? null : value;
  }
  return patch;
}

